import React, { useState } from 'react';
import Modal2 from 'components/commons/modal/Modal2';
import { range, rangeDesc } from 'utils/arrays';
import { updateUser } from 'config/api';
import {
  AnnualIncomeDropDownList,
  AnnualIncomeDropDownType,
  UserTag,
  annualIncomeList,
  getUnit,
} from 'components/features/utils/personaChart';
import { formatDateForString } from 'utils/formatter';

type NotSelected = -1;

const annualIncomeDropDownList = [
  { type: -1, labelFrom: '選択してください', labelTo: '', tag: null },
  ...annualIncomeList,
] as AnnualIncomeDropDownList[];

export const PersonaRegistModal = ({
  hide,
  isShow,
  registBirthday,
  registAnnualIncome,
  registInvestmentStartDate,
  getUserInfo,
  buttonText,
  isFirst,
  close,
}: {
  hide: () => void;
  isShow: boolean;
  registBirthday: string | null;
  registAnnualIncome: UserTag | null;
  registInvestmentStartDate: string | null;
  getUserInfo?: () => void;
  buttonText: string;
  isFirst: boolean;
  close: () => void;
}) => {
  const getAnnualIncomeListTag = annualIncomeDropDownList.find(
    list => list.tag === registAnnualIncome
  );
  const initialAnnualIncome =
    registAnnualIncome !== null && getAnnualIncomeListTag !== undefined
      ? getAnnualIncomeListTag.type
      : -1;

  const [annualIncome, setAnnualIncome] = useState<
    AnnualIncomeDropDownType | NotSelected
  >(initialAnnualIncome);

  const registInvestmentStartYear =
    registInvestmentStartDate === null
      ? null
      : Number(formatDateForString(registInvestmentStartDate, 'yyyy'));
  const thisYear = new Date().getFullYear();
  const initialInvestmentStartYear =
    registInvestmentStartYear !== null
      ? registInvestmentStartYear
      : thisYear - 5;
  const [investmentStartYear, setInvestmentStartYear] = useState<number>(
    initialInvestmentStartYear
  );

  const annualIncomeStates = annualIncomeDropDownList.map(list => list.type);
  const annualIncomeLabel = (type: AnnualIncomeDropDownType | NotSelected) => {
    const info = annualIncomeDropDownList.find(list => type === list.type);
    if (info !== undefined) {
      const unit = info.tag !== null ? getUnit(info.tag) : '';
      const label =
        info.tag === null || info.tag === 1400
          ? `${info.labelFrom}${unit}`
          : `${info.labelFrom}～${info.labelTo}${unit}`;
      return label;
    } else {
      return '';
    }
  };
  const annualIncomeFromString = (str: string) => {
    const num = Number(str);
    return num as AnnualIncomeDropDownType | NotSelected;
  };
  const toString = (value: number) => `${value.toString()}`;

  const unregisteredValue = -1;
  const NotSelect = () => (
    <option value={unregisteredValue}>選択してください</option>
  );

  // 生年月日項目
  const UnregistedOption = () => <option value={unregisteredValue}>‐</option>;
  const userBirthday =
    registBirthday !== null ? new Date(registBirthday) : null;
  const initialBirthday = {
    year: userBirthday !== null ? userBirthday.getFullYear() : thisYear - 30,
    month: userBirthday !== null ? userBirthday.getMonth() : unregisteredValue,
    day: userBirthday !== null ? userBirthday.getDate() : unregisteredValue,
  };
  const [birthday, setBirthday] = useState(initialBirthday);

  const changeYear = (year: number): void => {
    setBirthday(prev => {
      return {
        ...prev,
        year,
      };
    });
  };

  const changeMonth = (month: number): void => {
    setBirthday(prev => {
      return {
        ...prev,
        month,
      };
    });
  };

  const changeDay = (day: number): void => {
    setBirthday(prev => {
      return {
        ...prev,
        day,
      };
    });
  };

  const isDisabledRegister =
    birthday.day === unregisteredValue ||
    investmentStartYear === -1 ||
    annualIncome === -1;

  const registData = async () => {
    const annualIncomeItem = annualIncomeList.find(
      list => list.type === annualIncome
    );
    const annualIncomeTag =
      annualIncomeItem === undefined ||
      annualIncomeItem === null ||
      annualIncomeItem.type === 102
        ? 0
        : annualIncomeItem.tag;

    await updateUser({
      Birthday: `${birthday.year}-${birthday.month + 1}-${birthday.day}`,
      AnnualIncome: annualIncomeTag,
      InvestmentStartDate: `${investmentStartYear}/1/1`,
    });

    if (getUserInfo !== undefined) {
      await getUserInfo();
    }
    close();
  };

  const modalClose = () => {
    if (isFirst) {
      hide();
    } else {
      setAnnualIncome(initialAnnualIncome);
      setInvestmentStartYear(initialInvestmentStartYear);
      setBirthday(initialBirthday);
      hide();
    }
  };

  return (
    <>
      <Modal2 isShow={isShow}>
        <div className="p-4 max-h-modal overflow-y-auto">
          <div className="flex flex-col">
            {isFirst ? (
              <>
                <div
                  className="icon-close ml-auto text-gray-500 cursor-pointer"
                  onClick={hide}
                />
                <div className="mt-2">
                  <img
                    src="/static/images/persona/registPersonal.svg"
                    className="w-full"
                    alt=""
                  />
                </div>
                <div className="mt-2 text-base font-bold">
                  自分と似ている人たちと資産を比較
                </div>
                <div className="mt-2 text-sm">
                  ペルソナチャートでは、自分と同じような人と資産状況を比較できます。
                  <br />
                  はじめに、あなたの情報を入力してください。
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-row">
                  <div className="text-base font-bold">あなたの情報</div>
                  <div
                    className="icon-close ml-auto text-gray-500 cursor-pointer"
                    onClick={modalClose}
                  />
                </div>
              </>
            )}
            <div>
              <div className="mt-3">
                <span className="text-sm font-bold text-bluegray-500">
                  あなたの生年月日
                </span>
                <span className="ml-1 text-xs text-red-500">※必須</span>
              </div>

              <div className="mt-1 flex flex-row">
                <div
                  className={`w-1/3 flex flex-row items-center h-10 rounded border mt-1 border-bluegray-400`}>
                  <select
                    className={`w-full h-full appearance-none bg-transparent pl-4 text-base cursor-pointer`}
                    value={birthday.year}
                    onChange={event =>
                      changeYear(Number(event.currentTarget.value))
                    }>
                    <UnregistedOption />
                    {rangeDesc(thisYear - 100, thisYear).map((x, i) => (
                      <option key={i} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                  <div
                    className={'pointer-events-none flex items-center pr-1'}
                    style={{ left: '26%' }}>
                    <i
                      className={
                        'text-bluegray-400 pointer-events-none icon-chevron-down p-0.5'
                      }
                    />
                  </div>
                </div>
                <div
                  className={`w-1/3 ml-2 flex flex-row items-center h-10 rounded border mt-1 ${
                    birthday.year === unregisteredValue
                      ? 'border-bluegray-300'
                      : 'border-bluegray-400'
                  }`}>
                  <select
                    className={`w-full h-full appearance-none bg-transparent pl-4 text-base ${
                      birthday.year === unregisteredValue
                        ? 'text-bluegray-300'
                        : ''
                    } cursor-pointer`}
                    value={birthday.month}
                    onChange={event =>
                      changeMonth(Number(event.currentTarget.value))
                    }
                    disabled={birthday.year === unregisteredValue}>
                    <UnregistedOption />
                    {range(1, 12).map((x, i) => (
                      <option key={i} value={x - 1}>
                        {x}
                      </option>
                    ))}
                  </select>
                  <div
                    className={'pointer-events-none flex items-center pr-1'}
                    style={{ left: '58%' }}>
                    <i
                      className={`pointer-events-none icon-chevron-down p-0.5 ${
                        birthday.year === unregisteredValue
                          ? 'text-bluegray-300'
                          : 'text-bluegray-400'
                      }`}
                    />
                  </div>
                </div>
                <div
                  className={`w-1/3 ml-2 flex flex-row items-center h-10 rounded border mt-1 ${
                    birthday.month === unregisteredValue
                      ? 'border-bluegray-300'
                      : 'border-bluegray-400'
                  }`}>
                  <select
                    className={`w-full h-full appearance-none bg-transparent pl-4 text-base ${
                      birthday.month === unregisteredValue
                        ? 'text-bluegray-300'
                        : ''
                    } cursor-pointer`}
                    value={birthday.day}
                    onChange={event =>
                      changeDay(Number(event.currentTarget.value))
                    }
                    disabled={birthday.month === unregisteredValue}>
                    <UnregistedOption />
                    {range(
                      1,
                      new Date(birthday.year, birthday.month + 1, 0).getDate()
                    ).map((x, i) => (
                      <option key={i} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                  <div
                    className={'pointer-events-none flex items-center pr-1'}
                    style={{ right: '6%' }}>
                    <i
                      className={`pointer-events-none icon-chevron-down p-0.5 ${
                        birthday.month === unregisteredValue
                          ? 'text-bluegray-300'
                          : 'text-bluegray-400'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3">
              <div>
                <span className="text-sm font-bold text-bluegray-500">
                  年収
                </span>
                <span className="ml-1 text-xs text-red-500">※必須</span>
              </div>
              <div
                className={`flex flex-row overflow-hidden w-full items-center h-10 rounded border mt-1 border-bluegray-400`}>
                <select
                  className={`w-full h-full appearance-none bg-transparent text-base pl-4 cursor-pointer`}
                  value={annualIncome}
                  onChange={(e): void => {
                    const result = annualIncomeFromString(
                      e.currentTarget.value
                    );
                    setAnnualIncome(result);
                  }}>
                  {annualIncomeStates.map((s, i) => {
                    return (
                      <option key={i} value={toString(s)}>
                        {annualIncomeLabel(s)}
                      </option>
                    );
                  })}
                </select>
                <div
                  className={'pointer-events-none flex items-center pr-1'}
                  style={{ right: '8%' }}>
                  <i
                    className={`text-bluegray-400 cursor-pointer icon-chevron-down p-0.5 ml-auto`}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3">
              <div>
                <span className="text-sm font-bold text-bluegray-500">
                  投資を始めた年
                </span>
                <span className="ml-1 text-xs text-red-500">※必須</span>
              </div>
              <div
                className={`flex flex-row overflow-hidden w-full items-center h-10 rounded border mt-1 border-bluegray-400`}>
                <select
                  className={`w-full h-full appearance-none bg-transparent text-base pl-4 cursor-pointer`}
                  value={investmentStartYear}
                  onChange={event =>
                    setInvestmentStartYear(Number(event.currentTarget.value))
                  }>
                  <NotSelect />
                  {rangeDesc(thisYear - 100, thisYear).map((x, i) => (
                    <option key={i} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
                <div
                  className={'pointer-events-none flex items-center pr-1'}
                  style={{ right: '8%' }}>
                  <i
                    className={`text-bluegray-400 cursor-pointer icon-chevron-down p-0.5 ml-auto`}
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 text-xs text-bluegray-500">
              ※ここで入力された情報はペルソナチャートの統計情報として使用されるほか、当社のプライバシーポリシーに則って使用されます。
            </div>

            <button
              className={`mt-6 bg-tradewell rounded-lg shadow text-white py-2 text-center flex flex-row items-center gap-2 justify-center text-base font-bold ${
                isDisabledRegister ? 'opacity-20' : ''
              }`}
              disabled={isDisabledRegister}
              onClick={registData}>
              {buttonText}
            </button>
            <button
              className={`mt-4 p-2 text-bluegray-500 text-center text-sm`}
              onClick={modalClose}>
              キャンセル
            </button>
          </div>
        </div>
      </Modal2>
    </>
  );
};
